import { useClient } from '@/composables/useClient'
import { HttpError } from '@/queries/auth-error'
import { entityKeys } from '@/queries/entity-keys'
import { mutationOptions } from '@/queries/mutation-options'
import type { DeliveryDateSelection } from '@/types/delivery'
import type { EnrichedOrder, UpdateOrderDeliveryInfo } from '@repo/api-types'
import { infiniteQueryOptions, queryOptions } from '@tanstack/vue-query'
import { noop } from 'lodash'

const orderKeys = {
  list: () => [...entityKeys.order()] as const,
  past: () => [...entityKeys.pastOrder()] as const,
  byId: (orderId: string) => [...entityKeys.order(), orderId] as const,
  delayedDeliveryDate: (orderId: string, weeks: number) =>
    [...entityKeys.order(), orderId, 'delayed-delivery-date', weeks] as const,
  nextAvailableDeliveryDate: (orderId: string) =>
    [...entityKeys.order(), orderId, 'next-available-delivery-date'] as const,
}

export const orderQueries = {
  list: () =>
    queryOptions({
      queryKey: orderKeys.list(),
      queryFn: async () => {
        const client = await useClient()
        const { status, body } = await client.order.getOrders()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch orders')
      },
    }),

  past: () =>
    infiniteQueryOptions({
      queryKey: orderKeys.past(),
      queryFn: async ({ pageParam }): Promise<{ data: EnrichedOrder[]; hasNext: boolean }> => {
        const client = await useClient()
        const { status, body } = await client.order.getPastOrders({ query: { page: pageParam } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch past orders')
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => (lastPage.hasNext ? allPages.length : undefined),
    }),

  getById: (orderId: string) =>
    queryOptions({
      queryKey: orderKeys.byId(orderId),
      queryFn: async (): Promise<EnrichedOrder> => {
        const client = await useClient()
        const { status, body } = await client.order.getOrderById({ params: { orderId } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch order')
      },
    }),

  delayedDeliveryDate: (orderId: string, weeks: number) =>
    queryOptions({
      queryKey: orderKeys.delayedDeliveryDate(orderId, weeks),
      queryFn: async (): Promise<DeliveryDateSelection> => {
        const client = await useClient()
        const { status, body } = await client.order.getDelayedDeliveryDate({ params: { orderId }, query: { weeks } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not read delayed delivery date')
      },
    }),

  nextAvailableDeliveryDate: (orderId: string) =>
    queryOptions({
      queryKey: orderKeys.nextAvailableDeliveryDate(orderId),
      queryFn: async (): Promise<DeliveryDateSelection | null> => {
        const client = await useClient()
        const { status, body } = await client.order.getNextAvailableDeliveryDate({ params: { orderId } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not read next available delivery date')
      },
    }),
}

export const orderMutations = {
  addOneOffExtras: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        extras,
        deleteSubscriptionExtras,
      }: {
        orderId: string
        subscriptionId: string
        extras: { productVariantId: string; quantity: number }[]
        deleteSubscriptionExtras?: boolean
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.addOneOffExtras({
          params: { orderId },
          body: { subscriptionId, extras, deleteSubscriptionExtras },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to add one-off extra to order')
      },
      invalidation: {
        awaited: [{ entity: 'order' }, { entity: 'subscription' }],
      },
    }),

  updateOneOffExtra: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        orderLineItemId,
        quantity,
      }: {
        orderId: string
        subscriptionId: string
        orderLineItemId: string
        quantity: number
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.updateOneOffExtra({
          params: { orderId },
          body: { subscriptionId, orderLineItemId, quantity },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update one-off order extra')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),

  deleteOneOffExtra: () =>
    mutationOptions({
      mutationFn: async ({
        orderId,
        subscriptionId,
        orderLineItemId,
      }: {
        orderId: string
        subscriptionId: string
        orderLineItemId: string
      }) => {
        const client = await useClient()
        const { status, body } = await client.order.deleteOneOffExtra({
          params: { orderId },
          body: { subscriptionId, orderLineItemId },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to delete one-off order extra')
      },
      invalidation: { awaited: [{ entity: 'order' }] },
    }),

  updateOrderDeliveryInfo: () =>
    mutationOptions({
      mutationFn: async ({ orderId, deliveryInfo }: { orderId: string; deliveryInfo: UpdateOrderDeliveryInfo }) => {
        const client = await useClient()
        const { status, body } = await client.order.updateOrderDeliveryInfo({
          params: { orderId },
          body: deliveryInfo,
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update delivery info')
      },
      invalidation: { awaited: [{ entity: 'address' }], background: [{ entity: 'order' }] },
    }),

  updateOrderDeliveryInfoPreview: () =>
    mutationOptions({
      mutationFn: async ({ orderId, postcode }: { orderId: string; postcode: string }) => {
        const client = await useClient()
        const { status, body } = await client.order.updateOrderDeliveryInfoPreview({
          params: { orderId },
          body: { postcode },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not calculate the price for the new address')
      },
      invalidation: 'skip',
    }),

  retryPayment: () =>
    mutationOptions({
      mutationFn: async ({ orderId }: { orderId: string }) => {
        const client = await useClient()
        const { status, body } = await client.payment.retryPayment({ body: { orderId } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to retry payment')
      },
      onError: noop,
      invalidation: { awaited: [{ entity: 'order' }] },
    }),
}
